// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import { Link } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OneTrust from "@rc/App/Components/OneTrust/OneTrust";
import { createLinkIconStyles } from "@rc/App/SharedStyles/sharedStyles";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { getSettingsCollection } from "@rc/App/Store/Selectors/SettingsSelectors";
import { HeaderSize } from "@rc/Overrides/Pages/Header";
import { screen } from "@rc/XDL/Helpers";
import ArrowRedLeftInactive2 from "@rc/XDL/Icons/ArrowRedLeftInactive2";

import * as React from "react";
import { ReactNode } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

const mapStateToProps = (state: CustomAppState) => {
    const pageType: any = getCurrentPage(state).type;

    return {
        isPdpPage: pageType === "ProductDetailsPage",
        isCheckoutPages: pageType === "CheckoutShippingPage" || pageType === "CheckoutReviewAndSubmitPage",
        isChangeCustomerPage: pageType === "ChangeCustomerPage",
        isOrderConfirmationPage: pageType === "OrderConfirmationPage",
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        oneTrustJsLink: (getSettingsCollection(state).customSetting as any)?.oneTrustJsLink,
    };
};

export interface PageLayoutStyles {
    page?: InjectableCss;
    returnToCartLink?: LinkPresentationProps;
}

export const styles: PageLayoutStyles = {
    page: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin-bottom: 10px;
            margin-top: 12px;
            @media (max-width: 768px) {
                position: static !important;
            }
            @media print {
                display: none !important;
            }
        `,
    },
    returnToCartLink: {
        ...createLinkIconStyles(ArrowRedLeftInactive2, undefined, false, "#E2001A"),
    },
};

type Props = PageLayoutProps & ReturnType<typeof mapStateToProps> & HasHistory;

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,
    isCheckoutPages,
    isOrderConfirmationPage,
    history,
    cartPageUrl,
    isChangeCustomerPage,
    oneTrustJsLink,
}: Props) => {
    const navigateToCartPage = () => {
        if (cartPageUrl) {
            history.push(cartPageUrl);
        }
    };

    React.useEffect(() => {
        if (typeof document !== undefined) {
            if (isChangeCustomerPage) {
                document.querySelector("body") && document.querySelector("body")!.classList.add("customer-page");
            } else {
                document.querySelector("body") && document.querySelector("body")!.classList.remove("customer-page");
            }
        }
    }, [isChangeCustomerPage]);

    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-direction: column;
                position: relative;
                min-height: ${isChangeCustomerPage ? "auto" : "100vh"};
                @media print {
                    display: block !important;
                }
            `}
        >
            {oneTrustJsLink && <OneTrust />}
            {showHeader && header}

            <StyledWrapper
                css={css`
                    background-color: ${getColor("common.white")};
                    padding-top: ${showHeader
                        ? isCheckoutPages || isOrderConfirmationPage
                            ? HeaderSize.checkoutHeader
                            : isChangeCustomerPage
                            ? HeaderSize.changeCustomerHeader
                            : HeaderSize.default
                        : 25}px;
                    padding-bottom: 65px;
                    min-height: inherit;
                    flex-grow: 1;

                    ${screen("lte", "md")`
                        padding-top: ${
                            showHeader
                                ? isCheckoutPages || isOrderConfirmationPage
                                    ? HeaderSize.checkoutMobileHeader
                                    : HeaderSize.mobile
                                : 25
                        }px;
                    `}

                    @media print {
                        padding-top: 0;
                        min-height: auto;
                    }
                `}
                className="main-page"
            >
                {showBreadcrumbs && !(isCheckoutPages || isOrderConfirmationPage) && (
                    <Page as="div" {...styles.page}>
                        <PageBreadcrumbs />
                    </Page>
                )}
                {isCheckoutPages && (
                    <Page
                        as="div"
                        css={css`
                            ${styles.page?.css}
                            margin-bottom: 20px;
                        `}
                    >
                        <Link {...styles.returnToCartLink} onClick={navigateToCartPage}>
                            {translate("Return to Cart")}
                        </Link>
                    </Page>
                )}
                {pageContent}
            </StyledWrapper>
            
            <StyledWrapper
                css={css`
                    margin-top: ${isCheckoutPages ? "0" : "auto"};
                `}
            >
                {showFooter && footer}
                {showStorePickupOverlay && storePickupOverlay}
                {showAddressBookOverlay && addressBookOverlay}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withHistory(PageLayout));
